import _isEmpty from "lodash/isEmpty";

export function serializeResponse(hit) {
  const result = {};
  if (!_isEmpty(hit)) {
    result["user_pid"] = hit.id.toString();
    result["links"] = hit.links;
    if (!_isEmpty(hit.metadata)) {
      result["metadata"] = hit.metadata;

      // User type information for filtering and display
      if (hit.metadata.user_type) {
        result["metadata"]["user_type"] = hit.metadata.user_type;
      }

      if (hit.metadata.patron_type) {
        result["metadata"]["patron_type"] = hit.metadata.patron_type;
      }

      if (hit.metadata.role) {
        result["metadata"]["role"] = hit.metadata.role;
      }

      // Display type field that prioritizes user_type over role
      const displayType =
        hit.metadata.user_type || hit.metadata.role || "patron";
      result["metadata"]["display_type"] = displayType;

      // User category for easier filtering (normalized type)
      result["metadata"]["user_category"] = normalizeUserCategory(displayType);
    }
  }
  return result;
}

/**
 * Normalize user type/role to standard categories
 * @param {string} type - The user type or role
 * @returns {string} - Normalized category
 */
function normalizeUserCategory(type) {
  if (!type) return "patron";

  const typeLoower = type.toLowerCase();

  // Map variations to standard categories
  const categoryMap = {
    student: "student",
    faculty: "faculty",
    librarian: "librarian",
    admin: "admin",
    administrator: "admin",
    visitor: "visitor",
    researcher: "researcher",
    system: "system",
    anonymous: "anonymous",
  };

  return categoryMap[typeLoower] || "patron";
}

/**
 * Get user type display name for UI
 * @param {Object} metadata - Patron metadata
 * @returns {string} - Display name
 */
export function getUserTypeDisplayName(metadata) {
  if (!metadata) return "Patron";

  const userType = metadata.user_type || metadata.role || "patron";

  const displayNames = {
    student: "Student",
    faculty: "Faculty",
    librarian: "Librarian",
    admin: "Administrator",
    administrator: "Administrator",
    visitor: "Visitor",
    researcher: "Researcher",
    system: "System",
    anonymous: "Anonymous",
    patron: "Patron",
  };

  return displayNames[userType.toLowerCase()] || "Patron";
}

/**
 * Get user type badge color for UI
 * @param {Object} metadata - Patron metadata
 * @returns {string} - CSS class or color
 */
export function getUserTypeBadgeColor(metadata) {
  if (!metadata) return "secondary";

  const category =
    metadata.user_category ||
    normalizeUserCategory(metadata.user_type || metadata.role);

  const colorMap = {
    student: "primary",
    faculty: "success",
    librarian: "info",
    admin: "danger",
    visitor: "warning",
    researcher: "secondary",
    system: "dark",
    anonymous: "light",
    patron: "secondary",
  };

  return colorMap[category] || "secondary";
}

/**
 * Check if user has specific type or role
 * @param {Object} metadata - Patron metadata
 * @param {string} targetType - Type to check for
 * @returns {boolean}
 */
export function hasUserType(metadata, targetType) {
  if (!metadata || !targetType) return false;

  const targetLower = targetType.toLowerCase();
  const userType = (metadata.user_type || "").toLowerCase();
  const role = (metadata.role || "").toLowerCase();
  const category = (metadata.user_category || "").toLowerCase();

  return (
    userType === targetLower || role === targetLower || category === targetLower
  );
}

export const serializer = {
  fromJSON: serializeResponse,
};

// Export utility functions for use in components
export const patronUtils = {
  getUserTypeDisplayName,
  getUserTypeBadgeColor,
  hasUserType,
  normalizeUserCategory,
};
